import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useNotifications = (userRole) => {
  const [permission, setPermission] = useState('default');
  const [lastQuoteCount, setLastQuoteCount] = useState(0);
  const [newQuotesCount, setNewQuotesCount] = useState(0);
  const [pendingQuotesCount, setPendingQuotesCount] = useState(0);
  const [isEnabled, setIsEnabled] = useState(false);

  // Check if user has admin/employee role
  const hasAccess = userRole === 'admin' || userRole === 'employee';

  // Check if notifications are supported
  const isSupported = 'Notification' in window;

  // Request permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        setIsEnabled(true);
        localStorage.setItem('notifications_enabled', 'true');
        // Show confirmation notification
        new Notification('Elementary Cargo', {
          body: 'Notifications enabled! You will receive alerts for new quotes.',
          icon: '/favicon.ico',
          tag: 'notification-enabled'
        });
      }
      return result === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }, [isSupported]);

  // Send notification
  const sendNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') return;
    
    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        vibrate: [200, 100, 200],
        ...options
      });

      notification.onclick = () => {
        window.focus();
        window.location.href = '/admin';
        notification.close();
      };

      // Auto close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  }, [isSupported, permission]);

  // Check for new quotes - now using httpOnly cookies via withCredentials
  const checkNewQuotes = useCallback(async () => {
    if (!hasAccess) return;
    
    try {
      const response = await axios.get(`${API}/quotes/pending-count`);
      const pendingCount = response.data.pending_count || 0;
      
      // Update pending count
      setPendingQuotesCount(pendingCount);
      
      // If notifications are enabled and we have more pending quotes than before
      if (isEnabled && lastQuoteCount > 0 && pendingCount > lastQuoteCount) {
        const newCount = pendingCount - lastQuoteCount;
        
        sendNotification('New Quote Request!', {
          body: `${newCount} new quote${newCount > 1 ? 's' : ''} waiting for review`,
          tag: `quote-new-${Date.now()}`,
          requireInteraction: true
        });
        
        setNewQuotesCount(prev => prev + newCount);
      }
      
      setLastQuoteCount(pendingCount);
    } catch (error) {
      // Silently fail if unauthorized
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        console.error('Failed to check for new quotes:', error);
      }
    }
  }, [hasAccess, isEnabled, lastQuoteCount, sendNotification]);

  // Initialize
  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
      const stored = localStorage.getItem('notifications_enabled');
      if (stored === 'true' && Notification.permission === 'granted') {
        setIsEnabled(true);
      }
    }
  }, [isSupported]);

  // Poll for new quotes every 30 seconds (only if has access)
  useEffect(() => {
    if (!hasAccess) return;
    
    // Initial check
    checkNewQuotes();
    
    const interval = setInterval(checkNewQuotes, 30000);
    return () => clearInterval(interval);
  }, [hasAccess, checkNewQuotes]);

  // Clear new quotes count
  const clearNewQuotes = useCallback(() => {
    setNewQuotesCount(0);
  }, []);

  // Disable notifications
  const disableNotifications = useCallback(() => {
    setIsEnabled(false);
    localStorage.removeItem('notifications_enabled');
  }, []);

  return {
    isSupported,
    permission,
    isEnabled,
    hasAccess,
    newQuotesCount,
    pendingQuotesCount,
    requestPermission,
    sendNotification,
    clearNewQuotes,
    disableNotifications
  };
};

export default useNotifications;
